"use client";

import { useState, useEffect } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BTirDesPontsSoutenirDesVies() {
  const [isEnglish, setIsEnglish] = useState(false);
  useEffect(() => {
    const checkLang = () => {
      const lang = localStorage.getItem("lacolonne_language") || "fr";
      setIsEnglish(lang === "en");
    };
    checkLang();
    window.addEventListener("storage", checkLang);
    window.addEventListener("languagechange", checkLang);
    return () => {
      window.removeEventListener("storage", checkLang);
      window.removeEventListener("languagechange", checkLang);
    };
  }, []);
  const {
    ref: line1Ref,
    isVisible: line1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: line2Ref,
    isVisible: line2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: line3Ref,
    isVisible: line3Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: metaRef,
    isVisible: metaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const metadata = [{
    label: "EDMONTON / AB / 53.5461° N"
  }, {
    label: isEnglish ? "FRANCOPHONE COMMUNITY" : "COMMUNAUTÉ FRANCOPHONE"
  }, {
    label: isEnglish ? "SOLIDARITY · INCLUSION · DIGNITY" : "SOLIDARITÉ · INCLUSION · DIGNITÉ"
  }];
  return <section data-section-id="4351" id="bâtir-des-ponts-soutenir-des-vies" className="bg-primary text-primary-foreground relative min-h-screen w-full flex flex-col justify-between overflow-hidden">
      {/* Background image with high-contrast dark overlay */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/Copie de Carte Joyeux Anniversaire Feuilles Verte.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/Copie de Carte Joyeux Anniversaire Feuilles Verte.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/Copie de Carte Joyeux Anniversaire Feuilles Verte.png-wvc-sizes" alt={isEnglish ? "Portrait of a woman from the supportive community" : "Portrait d'une femme de la communauté solidaire"} loading="lazy" decoding="async" className="w-full h-full object-cover object-center md:object-[75%_center] brightness-[0.75] contrast-[1.25] saturate-[1.05]" />
        {/* Dark contrast overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/60 md:w-4/5" />
        <div className="absolute inset-0 bg-primary/40 backdrop-brightness-90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,hsl(220_20%_8%/0.85)_100%)]" />
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='1'/></svg>\")"
      }} />
      </div>

      <style>{`
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Main Content Grid */}
      <div className="relative z-10 mx-auto w-full max-w-[1440px] flex-1 flex flex-col justify-between px-6 pt-32 pb-12 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-6 my-auto">
          <div className="col-span-12 lg:col-span-8 flex flex-col justify-center">
            <div ref={eyebrowRef} className={`font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-primary-foreground/75 mb-8 md:mb-10 transition-all duration-700 ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="text-secondary font-semibold">EST. 2012</span>
              <span className="mx-3 text-secondary/60">—</span>
              <span>{isEnglish ? "LA COLONNE ASSOCIATION / EDMONTON" : "ASSOCIATION LA COLONNE / EDMONTON"}</span>
            </div>

            {/* Enlarged and emboldened heading */}
            <h1 className="font-default font-extrabold leading-[1.05] tracking-tight text-white mb-8 max-w-4xl" style={{
            fontSize: "clamp(44px, 7vw, 92px)"
          }}>
              <div className="overflow-hidden">
                <div ref={line1Ref} className={`transition-all duration-1000 ease-out ${line1Visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                  {isEnglish ? "Aging with" : "Vieillir dans la"}
                </div>
              </div>
              <div className="overflow-hidden">
                <div ref={line2Ref} className={`transition-all duration-1000 ease-out delay-200 ${line2Visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                  <em className="italic font-serif text-secondary drop-shadow-sm">
                    {isEnglish ? "dignity" : "dignité"}
                  </em>
                  ,
                </div>
              </div>
              <div className="overflow-hidden">
                <div ref={line3Ref} className={`transition-all duration-1000 ease-out delay-500 ${line3Visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                  {isEnglish ? "inclusion and sharing" : "l’inclusion et le partage"}
                </div>
              </div>
            </h1>

            <div ref={bodyRef} className={`max-w-[620px] mb-10 transition-all duration-700 delay-700 ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default text-lg md:text-xl leading-relaxed text-primary-foreground/85">
                {isEnglish ? "Association La Colonne is a francophone non-profit organization supporting diverse francophone seniors, women leaders, and multicultural families in Edmonton." : "L'Association La Colonne est un organisme francophone à but non lucratif qui accompagne les aînés francophones issus de la diversité, les femmes leaders et les familles multiculturelles à Edmonton."}
              </p>
            </div>

            {/* Rounded-2xl CTA buttons */}
            <div ref={ctaRef} className={`flex flex-wrap items-center gap-4 transition-all duration-700 delay-1000 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild size="lg" className="h-13 px-8 bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default text-sm font-semibold tracking-wider uppercase rounded-2xl shadow-lg transition-transform hover:-translate-y-1">
                <Link to="/BecomeMemberPage">
                  {isEnglish ? "Become member" : "Devenir membre"}
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="h-13 px-8 bg-primary/60 text-white hover:bg-white hover:text-primary font-default text-sm font-semibold tracking-wider uppercase border-white/30 rounded-2xl shadow-md transition-all hover:-translate-y-1">
                <Link to="/OurProgramsPage">
                  {isEnglish ? "Register for an activity" : "S'inscrire à une activité"}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="h-13 px-8 bg-secondary/15 text-secondary border border-secondary hover:bg-secondary hover:text-secondary-foreground font-default text-sm font-semibold tracking-wider uppercase rounded-2xl shadow-md transition-all hover:-translate-y-1">
                <Link to="/DonatePage">
                  {isEnglish ? "Donate" : "Faire un don"}
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="ghost" className="h-13 px-8 text-primary-foreground hover:bg-white/10 hover:text-white font-default text-sm font-semibold tracking-wider uppercase border border-white/20 rounded-2xl shadow-md transition-all hover:-translate-y-1">
                <Link to="/VolunteerRegistrationPage" newTab>
                  {isEnglish ? "Become volunteer" : "Devenir bénévole"}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Meta Row */}
        <div ref={metaRef} className={`w-full pt-8 mt-12 transition-all duration-700 delay-[1200ms] ${metaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="h-px w-full bg-white/15 mb-6" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-8">
            {metadata.map((item, i) => <div key={i} data-index={i} className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-primary-foreground/70">
                {item.label}
              </div>)}
          </div>
        </div>
      </div>

      {/* Vertical Discover Marker */}
      <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-4">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-primary-foreground/60" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        animation: "scrollPulse 2.5s ease-in-out infinite"
      }}>
          {isEnglish ? "Discover" : "Découvrir"}
        </div>
        <div className="h-24 w-px bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </section>;
}